
    // ============================================
    // WebGPU Native Virtual World - Main Entry
    // ============================================
    
    // Import from lib modules
    import { 
      CONFIG, 
      initGPU, 
      createUniformBuffers,
      // Uniform data creators
      createCameraData,
      createTerrainData,
      createCharacterData,
      createLightData,
      createOceanData,
      createUnderwaterData,
      createGodRaysData,
      createBubblesData,
      createRainData,
      createLightningData,
      createSnowData,
      LIGHT_UNIFORMS_SIZE,
      GOD_RAYS_UNIFORMS_SIZE,
      BUBBLES_UNIFORMS_SIZE,
      loadTexture,
      initBasisTranscoder,
      isKTX2,
      createGrassTexture,
      createSandTexture,
      createRockTexture,
      createSnowTexture,
      createFlatNormalTexture,
      // Shaders
      skyShader,
      oceanShader,
      shadowTerrainShader,
      shadowCharacterShader,
      terrainShader,
      characterShader,
      underwaterShader,
      godRaysShader,
      bubblesShader,
      // Geometry
      createPlaneGeometry,
      createGeometryBuffers,
      // Math utilities
      perspective,
      lookAt,
      mul,
      identity,
      rotateY,
      translate,
      scale,
      getTerrainHeightAt,
      setHeightmap,
      // Lighting / Day-Night Cycle
      getLightDirection,
      getFormattedTime,
      // Systems
      createWeatherSystem,
      createInputSystem,
      createSoundSystem,
      createCharacterSystem,
      createNPCSystem,
      createNameplateSystem,
      createMovementSystem,
      createDayNightSystem,
      // Networking
      createPresenceSystem,
      createOfflinePresence,
      sanitizeName,
    } from './lib/index.js';

    // Make config globally accessible for debugging/tweaking
    window.CONFIG = CONFIG;

    // Remote players read orange; the local player stays cyan.
    const PEER_NAMEPLATE_COLOR = '#ffaa33';

    // How many remote players the world shows at once. Drives both the NPC
    // system's pre-allocated slots and the nameplate texture array.
    const PEER_SLOT_COUNT = 16;

    const loadingEl = document.getElementById('loading');
    const errorEl = document.getElementById('error');
    const errorMsg = document.getElementById('error-msg');
    const controlsEl = document.getElementById('controls');

    function showError(msg) {
      loadingEl.classList.add('hidden');
      errorEl.style.display = 'block';
      errorMsg.textContent = msg;
    }

    // Check WebGPU
    if (!navigator.gpu) {
      showError('WebGPU not supported. Please use Chrome 113+ or Edge 113+');
      throw new Error('WebGPU not supported');
    }

    // ============================================
    // Initialize Basis Transcoder FIRST (before WebGPU)
    // This ensures WASM memory is allocated before GPU resources
    // ============================================
    console.log('🔧 Pre-initializing Basis transcoder...');
    const basisReady = await initBasisTranscoder();
    if (basisReady) {
      console.log('✅ Basis transcoder pre-initialized successfully');
    } else {
      console.warn('⚠️ Basis transcoder not available - KTX2 loading may fall back to procedural textures');
    }

    // ============================================
    // Initialize WebGPU (Native)
    // ============================================
    const canvas = document.getElementById('canvas');
    let device, context, format;

    try {
      const gpu = await initGPU(canvas);
      device = gpu.device;
      context = gpu.context;
      format = gpu.format;
    } catch (e) {
      showError('Failed to initialize WebGPU: ' + e.message);
      throw e;
    }

    // ============================================
    // Create Uniform Buffers (Native WebGPU)
    // ============================================
    const buffers = createUniformBuffers(device);
    const cameraBuffer = buffers.camera;
    const terrainBuffer = buffers.terrain;
    const oceanBuffer = buffers.ocean;
    const characterBuffer = buffers.character;
    const underwaterBuffer = buffers.underwater;

    // ============================================
    // TEXTURE LOADING CONFIGURATION
    // Using CONFIG.textures for all texture URLs
    // ============================================

    // Load terrain textures - can use procedural or image textures
    let grassTexture;
    let grassNormalTexture;
    if (CONFIG.textures.useImageGrass) {
      try {
        grassTexture = await loadTexture(device, CONFIG.textures.grass);
        console.log('✅ Loaded grass texture from image:', CONFIG.textures.grass);
      } catch (e) {
        console.warn('⚠️ Could not load grass image, falling back to procedural:', e.message);
        grassTexture = createGrassTexture(device);
      }
      try {
        grassNormalTexture = await loadTexture(device, CONFIG.textures.grassNormal);
        console.log('✅ Loaded grass normal texture from image:', CONFIG.textures.grassNormal);
      } catch (e) {
        console.warn('⚠️ Could not load grass normal image:', e.message);
        grassNormalTexture = null;
      }
    } else {
      grassTexture = createGrassTexture(device);
      grassNormalTexture = null;
    }
    
    // Create a flat normal texture as fallback for grass if normal map failed to load
    if (!grassNormalTexture) {
      grassNormalTexture = createFlatNormalTexture(device);
    }
    
    // Sand texture from local file
    let sandTexture;
    let sandNormalTexture;
    if (CONFIG.textures.useImageSand) {
      try {
        sandTexture = await loadTexture(device, CONFIG.textures.sand);
        console.log('✅ Loaded sand texture from image:', CONFIG.textures.sand);
      } catch (e) {
        console.warn('⚠️ Could not load sand image, falling back to procedural:', e.message);
        sandTexture = createSandTexture(device);
      }
      try {
        sandNormalTexture = await loadTexture(device, CONFIG.textures.sandNormal);
        console.log('✅ Loaded sand normal texture from image:', CONFIG.textures.sandNormal);
      } catch (e) {
        console.warn('⚠️ Could not load sand normal image:', e.message);
        sandNormalTexture = null;
      }
    } else {
      sandTexture = createSandTexture(device);
      sandNormalTexture = null;
    }
    
    // Create a flat normal texture as fallback for sand if normal map failed to load
    if (!sandNormalTexture) {
      sandNormalTexture = createFlatNormalTexture(device);
    }
    
    // Rock texture from local files
    let rockTexture;
    let rockNormalTexture;
    if (CONFIG.textures.useImageRock) {
      try {
        rockTexture = await loadTexture(device, CONFIG.textures.rock);
        console.log('✅ Loaded rock texture from image:', CONFIG.textures.rock);
      } catch (e) {
        console.warn('⚠️ Could not load rock image, falling back to procedural:', e.message);
        rockTexture = createRockTexture(device);
      }
      try {
        rockNormalTexture = await loadTexture(device, CONFIG.textures.rockNormal);
        console.log('✅ Loaded rock normal texture from image:', CONFIG.textures.rockNormal);
      } catch (e) {
        console.warn('⚠️ Could not load rock normal image:', e.message);
        rockNormalTexture = null;
      }
    } else {
      rockTexture = createRockTexture(device);
      rockNormalTexture = null;
    }
    
    // Create a flat normal texture as fallback if normal map failed to load
    if (!rockNormalTexture) {
      rockNormalTexture = createFlatNormalTexture(device);
    }
    
    // Snow texture from local file
    let snowTexture;
    if (CONFIG.textures.useImageSnow) {
      try {
        snowTexture = await loadTexture(device, CONFIG.textures.snow);
        console.log('✅ Loaded snow texture from image:', CONFIG.textures.snow);
      } catch (e) {
        console.warn('⚠️ Could not load snow image, falling back to procedural:', e.message);
        snowTexture = createSnowTexture(device);
      }
    } else {
      snowTexture = createSnowTexture(device);
    }

    // Water bump/normal texture for realistic water surface
    let waterBumpTexture;
    try {
      waterBumpTexture = await loadTexture(device, CONFIG.textures.waterBump);
      console.log('✅ Loaded water bump texture from image:', CONFIG.textures.waterBump);
    } catch (e) {
      console.warn('⚠️ Could not load water bump image, creating flat normal:', e.message);
      waterBumpTexture = createFlatNormalTexture(device);
    }

    // Create sampler for water bump (needs repeat mode)
    const textureSampler = device.createSampler({
      magFilter: 'linear',
      minFilter: 'linear',
      mipmapFilter: 'linear',
      addressModeU: 'repeat',
      addressModeV: 'repeat',
      // Note: No anisotropic filtering - textures don't have mipmaps generated
    });

    // ============================================
    // Character System (Modularized)
    // ============================================
    const characterSystem = createCharacterSystem(device);

    // ============================================
    // NPC System (Modularized) - 100 Ready Player Me avatars
    // ============================================
    const npcSystem = createNPCSystem(device, {
      characterSystem,
      npcCount: CONFIG.npc.count,
      terrainSize: CONFIG.geometry.terrain.size,
      freezeRadius: CONFIG.npc.freezeRadius,
      wanderRadius: CONFIG.npc.wanderRadius,
      minSeparation: CONFIG.npc.minSeparation,
      baseSpeed: CONFIG.npc.baseSpeed,
      runSpeed: CONFIG.npc.runSpeed,
      peerSlotCount: PEER_SLOT_COUNT,
    });
    // Start loading NPCs asynchronously
    npcSystem.init();
    window.npcSystem = npcSystem;   // console handle (matches window.CONFIG)

    // ============================================
    // Nameplate System - Canvas2D rendered nameplates (Orillusion-style)
    // ============================================
    const nameplateSystem = createNameplateSystem(device, {
      cameraBuffer,
      maxInstances: CONFIG.npc.count + 1 + PEER_SLOT_COUNT, // NPCs + player + remote players
    });

    function formatState(state) {
      switch (state) {
        case 'idle': return 'Idle';
        case 'walking': return 'Walking';
        case 'running': return 'Running';
        case 'following': return 'Following';
        default: return state ? String(state) : '';
      }
    }

    // ============================================
    // Geometry Generation (using lib/geometry.js)
    // ============================================
    // Create geometry - large enough to reach horizon
    const terrain = createPlaneGeometry(CONFIG.geometry.terrain.size, CONFIG.geometry.terrain.segments);
    const ocean = createPlaneGeometry(CONFIG.geometry.ocean.size, CONFIG.geometry.ocean.segments);

    // Create GPU buffers using helper
    const terrainBuffers = createGeometryBuffers(device, terrain);
    const terrainVB = terrainBuffers.vertexBuffer;
    const terrainIB = terrainBuffers.indexBuffer;

    const oceanBuffers = createGeometryBuffers(device, ocean);
    const oceanVB = oceanBuffers.vertexBuffer;
    const oceanIB = oceanBuffers.indexBuffer;

    // ============================================
    // WGSL Shaders
    // ============================================

    // Sky Shader - Full sky dome with proper ray direction
    // ============================================
    // Shadow Map Configuration
    // ============================================
    const SHADOW_MAP_SIZE = CONFIG.shadows.mapSize;

    // Create shadow map depth texture
    const shadowMapTexture = device.createTexture({
      size: [SHADOW_MAP_SIZE, SHADOW_MAP_SIZE],
      format: 'depth32float',
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });

    const shadowMapSampler = device.createSampler({
      compare: 'less',
      magFilter: 'linear',
      minFilter: 'linear',
    });

    // Day/Night cycle configuration
    const DAY_CYCLE_DURATION = CONFIG.dayNight.cycleDuration;

    // [getLightDirection moved to lib/lighting.js]
    // getLightDirection is imported from lib/index.js

    // Initial light direction (will be updated each frame)
    let sunDirNorm = getLightDirection(0, DAY_CYCLE_DURATION).direction;
    let lightInfo = getLightDirection(0, DAY_CYCLE_DURATION);

    // [Shadow shaders moved to lib/shaders/shadow.wgsl.js]
    // shadowTerrainShader, shadowCharacterShader are imported from lib/index.js

    // [Sky shader moved to lib/shaders/sky.wgsl.js]
    // skyShader is imported from lib/index.js
    // (~1465 lines of inline WGSL code removed)

    // terrainShader is now imported from lib/shaders/terrain.wgsl.js
    // (Removed ~760 lines of inline WGSL code)

    // [Ocean shader moved to lib/shaders/oceanShader.wgsl.js]
    // oceanShader is imported from lib/index.js
    // (~1062 lines of inline WGSL code removed)

    // [Effects shaders moved to lib/shaders/effects.wgsl.js]
    // underwaterShader, godRaysShader, bubblesShader, rainShader, lightningShader, snowShader
    // are imported from lib/index.js

    // [Character shader moved to lib/shaders/character.wgsl.js]
    // characterShader is imported from lib/index.js

    // ============================================
    // Create Pipelines
    // ============================================
    // ============================================
    // Island heightmap - one PNG drives the terrain mesh, the shadow pass, the
    // ocean depth and the CPU physics. Loaded before any bind group needs it.
    // ============================================
    const heightmapBitmap = await createImageBitmap(await (await fetch(CONFIG.heightmap.url)).blob());
    const heightmapSize = heightmapBitmap.width;
    const heightmapTexture = device.createTexture({
      size: [heightmapSize, heightmapSize],
      format: 'rgba8unorm',
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT,
      label: 'Island Heightmap',
    });
    device.queue.copyExternalImageToTexture(
      { source: heightmapBitmap },
      { texture: heightmapTexture },
      [heightmapSize, heightmapSize],
    );

    // clamp-to-edge: past the map's border the open sea carries on forever.
    const heightmapSampler = device.createSampler({
      magFilter: 'linear',
      minFilter: 'linear',
      addressModeU: 'clamp-to-edge',
      addressModeV: 'clamp-to-edge',
    });

    // The CPU needs the same bytes for physics, NPCs and the camera.
    {
      const scratch = new OffscreenCanvas(heightmapSize, heightmapSize);
      const scratchCtx = scratch.getContext('2d', { willReadFrequently: true });
      scratchCtx.drawImage(heightmapBitmap, 0, 0);
      const { data } = scratchCtx.getImageData(0, 0, heightmapSize, heightmapSize);
      const texels = new Uint8Array(heightmapSize * heightmapSize);
      for (let i = 0; i < texels.length; i++) texels[i] = data[i * 4];   // red channel
      setHeightmap(texels, heightmapSize);
      console.log(`✅ Heightmap loaded: ${heightmapSize}×${heightmapSize}, covering ${CONFIG.heightmap.worldSize} world units`);
    }
    const heightmapView = heightmapTexture.createView();

    const cameraLayout = device.createBindGroupLayout({
      entries: [{ binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } }],
    });

    // Light buffer for shadow mapping (Native WebGPU)
    const lightBuffer = device.createBuffer({
      size: LIGHT_UNIFORMS_SIZE,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    // Shadow pipeline layouts
    const shadowTerrainLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.VERTEX, texture: { sampleType: 'float' } },      // Heightmap
        { binding: 2, visibility: GPUShaderStage.VERTEX, sampler: { type: 'filtering' } },
      ],
    });

    const shadowCharacterLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.VERTEX, buffer: { type: 'uniform' } },
        { binding: 2, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } },
      ],
    });

    // Terrain layout with textures and shadows
    const terrainLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 4, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 5, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 6, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
        { binding: 7, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 8, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'depth' } },
        { binding: 9, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'comparison' } },
        { binding: 10, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },  // Rock normal map
        { binding: 11, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },  // Grass normal map
        { binding: 12, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },  // Sand normal map
        { binding: 13, visibility: GPUShaderStage.VERTEX, texture: { sampleType: 'float' } },      // Heightmap
        { binding: 14, visibility: GPUShaderStage.VERTEX, sampler: { type: 'filtering' } },
      ],
    });

    const meshLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      ],
    });

    // Ocean layout
    const oceanLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },  // Water bump texture
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },    // Water sampler
        { binding: 4, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },     // Heightmap
        { binding: 5, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
      ],
    });

    // Character layout with texture, bone matrices, and shadows
    const characterLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
        { binding: 4, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } },
        { binding: 5, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 6, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'depth' } },
        { binding: 7, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'comparison' } },
      ],
    });

    const vertexLayout = {
      arrayStride: 20,
      attributes: [
        { shaderLocation: 0, offset: 0, format: 'float32x3' },
        { shaderLocation: 1, offset: 12, format: 'float32x2' },
      ],
    };

    // Character vertex layout (pos, normal, uv, joints, weights, color = 19 floats = 76 bytes).
    // The voxel avatar is textureless: per-vertex flat colour at location 5.
    const characterVertexLayout = {
      arrayStride: 76,
      attributes: [
        { shaderLocation: 0, offset: 0, format: 'float32x3' },   // position
        { shaderLocation: 1, offset: 12, format: 'float32x3' },  // normal
        { shaderLocation: 2, offset: 24, format: 'float32x2' },  // uv
        { shaderLocation: 3, offset: 32, format: 'float32x4' },  // joints
        { shaderLocation: 4, offset: 48, format: 'float32x4' },  // weights
        { shaderLocation: 5, offset: 64, format: 'float32x3' },  // per-vertex flat colour (voxel)
      ],
    };

    // Sky pipeline
    const skyModule = device.createShaderModule({ code: skyShader });
    const skyPipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [cameraLayout] }),
      vertex: { module: skyModule, entryPoint: 'vs' },
      fragment: { module: skyModule, entryPoint: 'fs', targets: [{ format }] },
      primitive: { topology: 'triangle-list' },
      depthStencil: { format: 'depth32float', depthWriteEnabled: false, depthCompare: 'less-equal' },
    });

    // Shadow pipelines (depth-only)
    const shadowTerrainModule = device.createShaderModule({ code: shadowTerrainShader });
    const shadowTerrainPipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [shadowTerrainLayout] }),
      vertex: { module: shadowTerrainModule, entryPoint: 'vs', buffers: [vertexLayout] },
      primitive: { topology: 'triangle-list', cullMode: 'back' },
      depthStencil: { format: 'depth32float', depthWriteEnabled: true, depthCompare: 'less', depthBias: 4, depthBiasSlopeScale: 4 },
    });

    const shadowCharacterModule = device.createShaderModule({ code: shadowCharacterShader });
    const shadowCharacterPipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [shadowCharacterLayout] }),
      vertex: { module: shadowCharacterModule, entryPoint: 'vs', buffers: [characterVertexLayout] },
      primitive: { topology: 'triangle-list', cullMode: 'back' },
      depthStencil: { format: 'depth32float', depthWriteEnabled: true, depthCompare: 'less', depthBias: 4, depthBiasSlopeScale: 4 },
    });

    // Terrain pipeline with textures
    const terrainModule = device.createShaderModule({ code: terrainShader });
    const terrainPipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [terrainLayout] }),
      vertex: { module: terrainModule, entryPoint: 'vs', buffers: [vertexLayout] },
      fragment: { module: terrainModule, entryPoint: 'fs', targets: [{ format }] },
      primitive: { topology: 'triangle-list', cullMode: 'back' },
      depthStencil: { format: 'depth32float', depthWriteEnabled: true, depthCompare: 'less' },
    });

    // Ocean pipeline
    const oceanModule = device.createShaderModule({ code: oceanShader });
    const oceanPipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [oceanLayout] }),
      vertex: { module: oceanModule, entryPoint: 'vs', buffers: [vertexLayout] },
      fragment: {
        module: oceanModule, entryPoint: 'fs',
        targets: [{
          format,
          blend: {
            color: { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha' },
            alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha' },
          },
        }],
      },
      primitive: { topology: 'triangle-list', cullMode: 'none' },  // 'none' to see ocean from below
      depthStencil: { format: 'depth32float', depthWriteEnabled: false, depthCompare: 'less' },
    });

    // Character pipeline
    const characterModule = device.createShaderModule({ code: characterShader });
    const characterPipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [characterLayout] }),
      vertex: { module: characterModule, entryPoint: 'vs', buffers: [characterVertexLayout] },
      fragment: {
        module: characterModule,
        entryPoint: 'fs',
        targets: [{
          format,
          blend: {
            color: { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha' },
            alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha' },
          },
        }],
      },
      primitive: { topology: 'triangle-list', cullMode: 'back' },
      depthStencil: { format: 'depth32float', depthWriteEnabled: true, depthCompare: 'less' },
    });

    // Underwater effect layout
    const underwaterLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
      ],
    });

    // Underwater pipeline - for post-processing effect
    const underwaterModule = device.createShaderModule({ code: underwaterShader });
    const underwaterPipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [underwaterLayout] }),
      vertex: { module: underwaterModule, entryPoint: 'vs' },
      fragment: { module: underwaterModule, entryPoint: 'fs', targets: [{ format }] },
      primitive: { topology: 'triangle-list' },
    });


    // Player terrain height — sampled on the CPU each frame via getTerrainHeightAt (math.js), which
    // mirrors the shader's getHeight, so the avatar walks exactly on the visible ground. No GPU
    // readback / compute pass is needed for physics.
    let gpuTerrainHeight = 0;

    // ============================================
    // Volumetric God Rays Pipeline (3D light shafts)
    // ============================================
    const godRaysModule = device.createShaderModule({ code: godRaysShader });

    const godRaysLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      ],
    });

    const godRaysPipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [godRaysLayout] }),
      vertex: { module: godRaysModule, entryPoint: 'vs', buffers: [] },
      fragment: {
        module: godRaysModule,
        entryPoint: 'fs',
        targets: [{
          format,
          blend: {
            color: { srcFactor: 'src-alpha', dstFactor: 'one' }, // Additive blending
            alpha: { srcFactor: 'one', dstFactor: 'one' },
          },
        }]
      },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      depthStencil: { format: 'depth32float', depthWriteEnabled: false, depthCompare: 'less' },
    });

    // God rays uniform buffer (Native WebGPU)
    const godRaysBuffer = device.createBuffer({
      size: GOD_RAYS_UNIFORMS_SIZE,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const godRaysBG = device.createBindGroup({
      layout: godRaysLayout,
      entries: [
        { binding: 0, resource: { buffer: cameraBuffer } },
        { binding: 1, resource: { buffer: godRaysBuffer } },
      ],
    });

    // God rays configuration (from CONFIG)
    const GOD_RAYS_CONFIG = CONFIG.godRays;

    // ============================================
    // BUBBLES PIPELINE - Underwater bubble particles
    // ============================================
    const bubblesModule = device.createShaderModule({ code: bubblesShader });

    const bubblesLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      ],
    });

    const bubblesPipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [bubblesLayout] }),
      vertex: { module: bubblesModule, entryPoint: 'vs', buffers: [] },
      fragment: {
        module: bubblesModule,
        entryPoint: 'fs',
        targets: [{
          format,
          blend: {
            color: { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha' }, // Alpha blending for translucent bubbles
            alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha' },
          },
        }]
      },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      depthStencil: { format: 'depth32float', depthWriteEnabled: false, depthCompare: 'less' },
    });

    // Bubbles uniform buffer (Native WebGPU)
    const bubblesBuffer = device.createBuffer({
      size: BUBBLES_UNIFORMS_SIZE,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const bubblesBG = device.createBindGroup({
      layout: bubblesLayout,
      entries: [
        { binding: 0, resource: { buffer: cameraBuffer } },
        { binding: 1, resource: { buffer: bubblesBuffer } },
      ],
    });

    // Bubbles configuration (from CONFIG)
    const BUBBLES_CONFIG = CONFIG.bubbles;

    // ============================================
    // WEATHER SYSTEM (Modularized)
    // ============================================
    const weatherSystem = createWeatherSystem(device, format, cameraBuffer);

    // Create render target texture for scene (to be used by underwater effect)
    let sceneTexture = device.createTexture({
      size: [canvas.width, canvas.height],
      format,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC,
    });

    const sceneSampler = device.createSampler({
      magFilter: 'linear',
      minFilter: 'linear',
    });



    // ============================================
    // Bind Groups (Native WebGPU)
    // ============================================
    const skyBG = device.createBindGroup({
      layout: cameraLayout,
      entries: [{ binding: 0, resource: { buffer: cameraBuffer } }],
    });

    // Shadow bind groups
    const shadowTerrainBG = device.createBindGroup({
      layout: shadowTerrainLayout,
      entries: [
        { binding: 0, resource: { buffer: lightBuffer } },
        { binding: 1, resource: heightmapView },
        { binding: 2, resource: heightmapSampler },
      ],
    });

    const terrainBG = device.createBindGroup({
      layout: terrainLayout,
      entries: [
        { binding: 0, resource: { buffer: cameraBuffer } },
        { binding: 1, resource: { buffer: terrainBuffer } },
        { binding: 2, resource: grassTexture.createView() },
        { binding: 3, resource: sandTexture.createView() },
        { binding: 4, resource: rockTexture.createView() },
        { binding: 5, resource: snowTexture.createView() },
        { binding: 6, resource: textureSampler },
        { binding: 7, resource: { buffer: lightBuffer } },
        { binding: 8, resource: shadowMapTexture.createView() },
        { binding: 9, resource: shadowMapSampler },
        { binding: 10, resource: rockNormalTexture.createView() },
        { binding: 11, resource: grassNormalTexture.createView() },
        { binding: 12, resource: sandNormalTexture.createView() },
        { binding: 13, resource: heightmapView },
        { binding: 14, resource: heightmapSampler },
      ],
    });

    // Ocean bind group (created dynamically in render loop)
    let oceanBG = null;

    // Shadow character bind group will be created after buffers are ready
    let shadowCharacterBG = null;

    // NPC bind groups created flag
    let npcBindGroupsCreated = false;

    // Ensure bone matrices buffer is ready from character system
    characterSystem.ensureBoneMatricesBuffer();

    // Character bind group will be created after texture loads
    let characterBG = null;

    // ============================================
    // Depth Texture (readable for underwater effect)
    // ============================================
    let depthTexture = device.createTexture({
      size: [canvas.width, canvas.height],
      format: 'depth32float',
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });

    // ============================================
    // Camera & Controls - Third Person
    // ============================================
    // Character position (the actual player)
    // Load saved state from localStorage or use defaults
    const savedPosition = localStorage.getItem('ovgrid_avatar_position');
    const savedRotation = localStorage.getItem('ovgrid_avatar_rotation');
    // Bounded world: ignore any stale / out-of-bounds saved position (e.g. a far or high spot
    // persisted before the island existed) so we never respawn into the jittery far coordinates.
    let charPos = { ...CONFIG.character.spawnPosition };
    if (savedPosition) {
      try {
        const p = JSON.parse(savedPosition);
        if (Number.isFinite(p?.x) && Number.isFinite(p?.y) && Number.isFinite(p?.z) &&
            Math.abs(p.x) <= 1700 && Math.abs(p.z) <= 1700 && p.y < 500) {
          charPos = p;
        }
      } catch (e) { /* corrupt save — keep spawn default */ }
    }

    let charRotation = savedRotation ? parseFloat(savedRotation) : 0;  // Character facing direction (yaw)
    let charTargetRotation = charRotation;  // Target rotation for smooth interpolation

    const camHeightOffset = CONFIG.camera.height;

    // ============================================
    // Input System (keyboard, mouse, touch)
    // ============================================
    const inputSystem = createInputSystem(canvas, {
      onKeyDown: (code, keys) => {
        // Cloud coverage controls
        if (code === 'Digit1') weatherSystem.adjustCloudCoverage(1);
        if (code === 'Digit2') weatherSystem.adjustCloudCoverage(-1);
        // Weather controls
        if (code === 'Digit3') weatherSystem.setWeather(3);  // Light rain
        if (code === 'Digit4') weatherSystem.setWeather(4);  // Heavy rain
        if (code === 'Digit5') weatherSystem.setWeather(5);  // Thunderstorm
        if (code === 'Digit6') weatherSystem.setWeather(6);  // Snow
        if (code === 'Digit0') weatherSystem.setWeather(0);  // Clear
        // Toggle UI with H
        if (code === 'KeyH') {
          const controls = document.getElementById('controls');
          controls.classList.toggle('hidden');
        }
      }
    });

    // Alias for quick access
    const keys = inputSystem.keys;
    
    // Movement key helpers from input system (WASD + Arrow keys)
    const { isKeyForward, isKeyBackward, isKeyLeft, isKeyRight, isAnyMovementKey } = inputSystem;

    // ============================================
    // Movement System (physics, swimming, flying)
    // ============================================
    const movementSystem = createMovementSystem(inputSystem);

    // ============================================
    // Day/Night System
    // ============================================
    const dayNightSystem = createDayNightSystem();

    // Save all state periodically (every 2 seconds)
    setInterval(() => {
      localStorage.setItem('ovgrid_avatar_position', JSON.stringify(charPos));
      localStorage.setItem('ovgrid_avatar_rotation', charRotation.toString());
      inputSystem.saveCameraState();
      dayNightSystem.saveState();
    }, 2000);

    // Save all state on page unload
    window.addEventListener('beforeunload', () => {
      localStorage.setItem('ovgrid_avatar_position', JSON.stringify(charPos));
      localStorage.setItem('ovgrid_avatar_rotation', charRotation.toString());
      inputSystem.saveCameraState();
      dayNightSystem.saveState();
    });

    // ============================================
    // Sound System
    // ============================================
    const soundSystem = createSoundSystem();

    // [Matrix math functions moved to lib/math.js]
    // perspective, lookAt, mul, identity, rotateY, translate, scale
    // are imported from lib/index.js

    // ============================================
    // Render Loop
    // ============================================
    let lastT = performance.now(), totalT = 0, frames = 0, fps = 0, fpsT = 0;

    // Frame cap. The gap between animation frames is far too jittery to test
    // directly (4-12 ms on a nominally 8.3 ms 120 Hz panel), so draw whenever we
    // are still under the target AVERAGE rate. That is self-correcting and never
    // drops to a lower harmonic the way a per-gap threshold does.
    const frameInterval = CONFIG.render.maxFPS > 0 ? 1000 / CONFIG.render.maxFPS : 0;
    let capStart = performance.now();
    let capDrawn = 0;
    
    // Initialize day/night UI controls
    dayNightSystem.initUI({
      checkbox: document.getElementById('auto-time'),
      slider: document.getElementById('time-slider'),
      display: document.getElementById('time-display')
    });

    // ============================================
    // Entry gate: pick a name, then join the world
    // ============================================
    loadingEl.classList.add('hidden');

    const enterDialog = document.getElementById('enter-world');
    const nameInput = document.getElementById('player-name-input');
    const peerCountEl = document.getElementById('peer-count');

    // These are read by the render loop, so they exist before it starts.
    let playerName = 'Player';
    let presence = createOfflinePresence();
    let lastPeerCount = -1;

    // Draw the world first and open the dialog over it: the island, the sea and
    // the NPC crowd are the argument for pressing the button. The player stays
    // put because input.js ignores keys typed into a field.
    render();

    nameInput.value = localStorage.getItem('wgpu_player_name') ?? '';
    enterDialog.showModal();
    await new Promise((done) => enterDialog.addEventListener('close', done, { once: true }));

    playerName = sanitizeName(nameInput.value);
    localStorage.setItem('wgpu_player_name', playerName);
    document.getElementById('player-name').textContent = playerName;

    // Show UI
    controlsEl.classList.remove('hidden');

    // P2P presence over GenosDB, once the player has actually entered. It
    // connects in the background; until it is up this stays a no-op.
    createPresenceSystem({ name: playerName }).then((system) => { presence = system; });
    window.presence = { getPeers: () => presence.getPeers(), get online() { return presence.online === true; } };

    function render() {
      const now = performance.now();

      // Already at the target rate for this window: reschedule, draw nothing.
      if (frameInterval && now - capStart < capDrawn * frameInterval) {
        requestAnimationFrame(render);
        return;
      }
      capDrawn++;
      if (now - capStart >= 1000) {   // restart the window so the counters stay small
        capStart = now;
        capDrawn = 1;
      }

      const dt = (now - lastT) / 1000;
      lastT = now;
      totalT += dt;
      
      // Update day/night cycle
      const dayTime = dayNightSystem.update(dt);

      // Update weather system
      weatherSystem.update(dt, totalT);

      // FPS
      frames++;
      fpsT += dt;
      if (fpsT >= 1) {
        fps = frames;
        frames = 0;
        fpsT = 0;
        document.getElementById('fps').textContent = fps;
      }

      // Get camera state from input system
      const camYaw = inputSystem.camYaw;
      const camPitch = inputSystem.camPitch;
      const camDistance = inputSystem.camDistance;
      const { fwdX, fwdZ, rightX, rightZ } = movementSystem.getCameraDirections(camYaw);

      // Sprint with Shift, Fly with Q
      const isSprinting = keys['ShiftLeft'] || keys['ShiftRight'];
      const isCurrentlyFlying = keys['KeyQ'];

      // Check if currently underwater for speed adjustment
      const waterLevel = movementSystem.waterLevel;
      const currentlyUnderwater = charPos.y < waterLevel;

      // Calculate speed based on state
      const speed = movementSystem.calculateSpeed(isCurrentlyFlying, currentlyUnderwater, isSprinting);

      // Movement input
      let moveX = 0, moveZ = 0, moveY = 0;

      // Check if deep enough to swim
      const isDeepEnoughToSwim = movementSystem.canSwim(charPos, gpuTerrainHeight);

      // Swimming: Full 3D movement based on camera direction (only when deep enough)
      if (isDeepEnoughToSwim && !isCurrentlyFlying) {
        const result = movementSystem.updateSwimming(charPos, dt, camYaw, camPitch, gpuTerrainHeight);
        moveX = result.moveX;
        moveY = result.moveY;
        moveZ = result.moveZ;
        if (result.charTargetRotation !== null) {
          charTargetRotation = result.charTargetRotation;
        }
      }
      // Normal ground/air movement (including shallow water walking)
      else if (!isCurrentlyFlying) {
        const result = movementSystem.updateGroundMovement(charPos, dt, camYaw, speed);
        moveX = result.moveX;
        moveZ = result.moveZ;
        if (result.charTargetRotation !== null) {
          charTargetRotation = result.charTargetRotation;
        }
      }

      // Track if moving for animation purposes
      const isMoving = Math.abs(moveX) > 0.001 || Math.abs(moveZ) > 0.001 || Math.abs(moveY) > 0.001;

      // Smoothly interpolate character rotation towards target
      charRotation = movementSystem.smoothRotation(charRotation, charTargetRotation, dt);

      // Flying with F - 3D movement like swimming
      if (isCurrentlyFlying) {
        movementSystem.updateFlying(charPos, dt, camYaw, camPitch, gpuTerrainHeight);
      }

      // Island physics height — CPU getTerrainHeightAt mirrors the terrain shader's getHeight, so the
      // player walks exactly on the visible ground (no GPU readback / compute pass needed).
      gpuTerrainHeight = getTerrainHeightAt(charPos.x, charPos.z);

      // Use GPU terrain height for physics (defaults to 0 on first frame)
      const terrainHeight = gpuTerrainHeight;
      const characterHeight = CONFIG.character.height;

      // Check if avatar is underwater (feet below water level)
      const isUnderwater = charPos.y < waterLevel;
      const waterDepth = waterLevel - charPos.y;  // How deep underwater

      // Update physics (gravity, jumping, swimming physics)
      movementSystem.updatePhysics(charPos, dt, terrainHeight, isDeepEnoughToSwim, isCurrentlyFlying);

      // Bounded world — the procedural island lives within a radius of ~500. Keep the player inside
      // ±600 (island + a little sea) so we never reach the large coordinates that would jitter.
      charPos.x = Math.max(-600, Math.min(600, charPos.x));
      charPos.z = Math.max(-600, Math.min(600, charPos.z));

      const isGrounded = movementSystem.isGrounded;

      // Update movement sounds
      const isFlying = isCurrentlyFlying;
      const isSwimming = isDeepEnoughToSwim && !isFlying;  // Swimming only when deep enough and not flying
      const isSprintSwimming = isSwimming && isSprinting;  // Fast swimming with Shift
      const isJumping = !isGrounded && movementSystem.velocityY > 0 && !isSwimming;
      const isFalling = !isGrounded && movementSystem.velocityY < 0 && !isFlying && !isSwimming;
      const inWater = terrainHeight < waterLevel && charPos.y < waterLevel + 1.0;
      soundSystem.updateMovementSounds(isMoving, isSprinting, isFlying, isJumping, inWater);

      // === P2P presence ===
      // Broadcast the local player (throttled to 12 Hz inside) and ease the
      // remote ones toward their last snapshot.
      presence.broadcast(charPos, charRotation, characterSystem.animationSystem?.currentAnimation?.name ?? 'Idle');
      presence.update(dt);
      const remotePlayers = presence.getPeers();
      if (remotePlayers.length !== lastPeerCount) {
        lastPeerCount = remotePlayers.length;
        peerCountEl.textContent = lastPeerCount;
      }

      // Update NPCs (AI behavior, animation, position) and the remote players
      if (npcSystem.isReady()) {
        // Use accurate terrain height calculation (matches shader)
        npcSystem.update(dt, charPos.x, charPos.y, charPos.z, getTerrainHeightAt);
        npcSystem.syncPeers(remotePlayers);   // before updateUniforms: it writes their model matrices
        npcSystem.updateUniforms(CONFIG.character.scale);  // Fixed: was modelScale (undefined)
        
        // Update HUD for NPCs (billboard nameplates)
        const npcs = npcSystem.getNPCs();
        const cameraPos = [charPos.x, charPos.y + characterHeight, charPos.z];
        
        // Include player avatar in HUD list
        const playerAvatar = {
          name: playerName,
          x: charPos.x,
          y: charPos.y,
          z: charPos.z,
          color: '#4ff3ff',
          health: 1.0,
          energy: 0.85,
          state: isSprinting ? 'running' : (isMoving ? 'walking' : 'idle')
        };
        const allCharacters = [playerAvatar, ...npcs];
        
        // Update nameplate system with all entities
        // Generate unique colors for each avatar using HSL
        const nameplateEntities = allCharacters.map((entity, index) => {
          let accentColor;
          if (index === 0) {
            // Player: cyan
            accentColor = '#4ff3ff';
          } else {
            // NPCs: unique color based on index using golden ratio for good distribution
            const hue = (index * 137.508) % 360; // Golden angle for even distribution
            accentColor = `hsl(${hue}, 70%, 55%)`;
          }
          return {
            id: entity.name || 'player',
            name: entity.name || 'Player',
            status: formatState(entity.state),
            x: entity.x,
            y: entity.y,
            z: entity.z,
            health: entity.health || 1.0,
            color: accentColor,
          };
        });
        // Keyed by slot, not by peer id: the key set is bounded by
        // PEER_SLOT_COUNT, so nameplate textures are reused across rejoins.
        // Two peers sharing a name still get one nameplate each.
        const peerEntities = npcSystem.getPeerSlots()
          .filter((slot) => slot.active)
          .map((slot) => ({
            id: slot.id,
            name: slot.name,
            status: formatState(slot.state),
            state: slot.state,
            x: slot.x,
            y: slot.y,
            z: slot.z,
            health: 1.0,
            color: PEER_NAMEPLATE_COLOR,
          }));
        nameplateSystem.update(nameplateEntities.concat(peerEntities), cameraPos);
      }

      // === Third Person Camera ===
      // Camera orbits around character
      // Zoom is straight horizontal, not affected by pitch angle
      const camOffsetX = -Math.sin(camYaw) * camDistance;
      const camOffsetY = Math.sin(-camPitch) * 8 + camHeightOffset;  // Height based on pitch, fixed distance
      const camOffsetZ = -Math.cos(camYaw) * camDistance;

      const camPos = {
        x: charPos.x + camOffsetX,
        y: charPos.y + characterHeight + camOffsetY,
        z: charPos.z + camOffsetZ
      };

      // Camera looks at character - height adjusts based on zoom distance
      // When zoomed in, look at face; when zoomed out, look at body center
      const zoomFactor = (camDistance - CONFIG.camera.minDistance) / (CONFIG.camera.maxDistance - CONFIG.camera.minDistance);
      const lookHeightMultiplier = CONFIG.camera.lookHeightNear +
        (CONFIG.camera.lookHeightFar - CONFIG.camera.lookHeightNear) * zoomFactor;
      const lookTarget = [charPos.x, charPos.y + characterHeight * lookHeightMultiplier, charPos.z];

      // Update UI
      document.getElementById('position').textContent = `${charPos.x.toFixed(0)}, ${charPos.y.toFixed(0)}, ${charPos.z.toFixed(0)}`;

      // Time display synced with day/night cycle
      const formattedTime = dayNightSystem.getFormattedTime();
      const timeDisplayEl = document.getElementById('time-display');
      if (timeDisplayEl) timeDisplayEl.textContent = formattedTime;

      // Matrices
      const proj = perspective(Math.PI / 3, canvas.width / canvas.height, 0.1, 15000);
      const view = lookAt([camPos.x, camPos.y, camPos.z], lookTarget, [0, 1, 0]);
      const vp = mul(proj, view);

      // Character model matrix (position + rotation + scale)
      const charScale = CONFIG.character.scale;
      const charModelMatrix = mul(
        mul(translate(charPos.x, charPos.y, charPos.z), rotateY(charRotation)),
        scale(charScale)
      );

      // Update uniform buffers (Native WebGPU)
      device.queue.writeBuffer(cameraBuffer, 0, createCameraData({
        viewProjection: vp,
        position: camPos,
        time: totalT,
        cloudCoverage: weatherSystem.cloudCoverage,
        starBlur: CONFIG.dayNight.starBlur,
        skyDarkening: weatherSystem.skyDarkening,
        lightningFlash: weatherSystem.lightningFlash,
        lightningPosX: weatherSystem.lightningPosX,
        lightningPosY: weatherSystem.lightningPosY,
        dayTime: dayTime,
      }));

      device.queue.writeBuffer(terrainBuffer, 0, createTerrainData({
        modelMatrix: identity(),
        color: [0.4, 0.6, 0.3],
        scale: 1
      }));

      // Ocean uniform buffer - all parameters from CONFIG
      const oc = CONFIG.ocean;
      device.queue.writeBuffer(oceanBuffer, 0, createOceanData({
        modelMatrix: identity(),
        deepColor: oc.deepColor,
        waterLevel: CONFIG.world.waterLevel,
        shallowColor: oc.shallowColor,
        time: totalT,
        underwaterTint: oc.underwaterTint,
        deepDepth: oc.deepDepthThreshold,
        skyReflection: oc.skyReflectionColor,
        shallowDepth: oc.shallowDepthThreshold,
        foamColor: oc.foamColor,
        veryShallowDepth: oc.veryShallowThreshold,
        sunColor: oc.sunColor,
        foamDepthMax: oc.foamDepthMax,
        sssColor: oc.sssColor,
        sssStrength: oc.sssStrength,
        causticsColor: oc.causticsColor,
        causticsIntensity: oc.causticsIntensity,
        baseAlphaMin: oc.baseAlphaMin,
        baseAlphaMax: oc.baseAlphaMax,
        depthAlphaRange: oc.depthAlphaRange,
        shallowAlphaBoost: oc.shallowAlphaBoost,
        distortionStrength: oc.distortionStrength,
        distortionScale: oc.distortionScale,
        distortionSpeed: oc.distortionSpeed,
        distortionWorldScale: oc.distortionWorldScale,
        causticsScale: oc.causticsScale,
        causticsSpeed: oc.causticsSpeed,
        fresnelF0: oc.fresnelF0,
        fresnelPower: oc.fresnelPower,
        sunDir: sunDirNorm,
        skyReflectionStr: oc.skyReflectionStrength,
        ambientLight: oc.ambientLight,
        diffuseLight: oc.diffuseLight,
        lightBoost: oc.lightBoost,
        foamLightBoost: oc.foamLightBoost,
        specPower: oc.specPower,
        specStrength: oc.specStrength,
        sparklePower: oc.sparklePower,
        sparkleStrength: oc.sparkleStrength,
        foamThreshold: oc.foamThreshold,
        foamEdgeStart: oc.foamEdgeStart,
        foamBandThreshold: oc.foamBandThreshold,
        foamEdgeStrength: oc.foamEdgeStrength,
        foamBandStrength: oc.foamBandStrength,
        foamAlphaBlend: oc.foamAlphaBlend,
        sssShallowBoost: oc.sssShallowBoost,
        sssPower: oc.sssPower,
        underwaterTintStr: oc.underwaterTintStrength,
        avatarPos: charPos,
        avatarHeight: CONFIG.character.scale * CONFIG.character.baseHeight,
      }));

      device.queue.writeBuffer(characterBuffer, 0, createCharacterData({
        modelMatrix: charModelMatrix,
        color: CONFIG.character.skinColor,
      }));

      // ============================================
      // Update Light Buffer for Shadow Mapping
      // ============================================
      // Update light direction based on time for dynamic day/night cycle
      // Uses sun during day, moon during night for shadow casting
      lightInfo = getLightDirection(dayTime, DAY_CYCLE_DURATION);
      sunDirNorm = lightInfo.direction;

      // Light projection - orthographic, follows character
      const SHADOW_DISTANCE = CONFIG.shadows.area;
      const SHADOW_NEAR = CONFIG.shadows.near;
      const SHADOW_FAR = CONFIG.shadows.far;

      // Light position - offset from character along light direction (sun or moon)
      // For moon shadows, we use a longer distance and lower intensity
      const lightDistance = lightInfo.isNight ? 180 : 150;
      const lightPosX = charPos.x + sunDirNorm[0] * lightDistance;
      const lightPosY = charPos.y + sunDirNorm[1] * lightDistance + 50;
      const lightPosZ = charPos.z + sunDirNorm[2] * lightDistance;

      // Orthographic projection for directional light
      const lightProj = new Float32Array([
        2 / SHADOW_DISTANCE, 0, 0, 0,
        0, 2 / SHADOW_DISTANCE, 0, 0,
        0, 0, -2 / (SHADOW_FAR - SHADOW_NEAR), 0,
        0, 0, -(SHADOW_FAR + SHADOW_NEAR) / (SHADOW_FAR - SHADOW_NEAR), 1
      ]);

      // Light view matrix - looking at character from light position
      const lightView = lookAt([lightPosX, lightPosY, lightPosZ], [charPos.x, charPos.y, charPos.z], [0, 1, 0]);
      const lightVP = mul(lightProj, lightView);

      device.queue.writeBuffer(lightBuffer, 0, createLightData({
        viewProjection: lightVP,
        position: charPos,
      }));

      // ============================================
      // Shadow Pass - Render depth from light's view
      // ============================================
      const enc = device.createCommandEncoder();

      // Check if camera is underwater
      const cameraUnderwater = camPos.y < waterLevel;
      const cameraDepth = Math.max(0, waterLevel - camPos.y);

      // Update underwater buffer if camera is underwater
      if (cameraUnderwater) {
        const uw = CONFIG.underwater;
        device.queue.writeBuffer(underwaterBuffer, 0, createUnderwaterData({
          cameraPos: camPos,
          time: totalT,
          waterLevel: waterLevel,
          cameraDepth: cameraDepth,
          fogDensity: uw.fogDensity,
          fogStart: uw.fogStart,
          fogEnd: uw.fogEnd,
          tintStrength: uw.tintStrength,
          godRaysIntensity: uw.godRaysIntensity,
          godRaysSpeed: uw.godRaysSpeed,
          godRaysDensity: uw.godRaysDensity,
          godRaysLength: uw.godRaysLength,
          causticsIntensity: uw.causticsIntensity,
          causticsScale: uw.causticsScale,
          causticsSpeed: uw.causticsSpeed,
          surfaceWaveStrength: uw.surfaceWaveStrength,
          surfaceFresnelPower: uw.surfaceFresnelPower,
          bubblesEnabled: uw.bubblesEnabled ? 1.0 : 0.0,
          bubblesCount: uw.bubblesCount,
          bubblesSpeed: uw.bubblesSpeed,
          bubblesSize: uw.bubblesSize,
          depthDarkenStart: uw.depthDarkenStart,
          depthDarkenEnd: uw.depthDarkenEnd,
          depthDarkenStrength: uw.depthDarkenStrength,
          blurRadius: uw.blurRadius,
          fogColor: uw.fogColor,
          tintColor: uw.tintColor,
          godRaysColor: uw.godRaysColor,
          surfaceColor: uw.surfaceColor,
          bubblesColor: uw.bubblesColor,
        }));
      }

      // Ocean pipeline
      // Then copy to screen or apply underwater effect
      const mainColorView = sceneTexture.createView();

      // Shadow pass
      const shadowPass = enc.beginRenderPass({
        colorAttachments: [],
        depthStencilAttachment: {
          view: shadowMapTexture.createView(),
          depthClearValue: 1.0,
          depthLoadOp: 'clear',
          depthStoreOp: 'store',
        },
      });

      // Render terrain to shadow map
      shadowPass.setPipeline(shadowTerrainPipeline);
      shadowPass.setBindGroup(0, shadowTerrainBG);
      shadowPass.setVertexBuffer(0, terrainVB);
      shadowPass.setIndexBuffer(terrainIB, 'uint32');
      shadowPass.drawIndexed(terrainBuffers.indexCount);

      // Render character to shadow map (if ready)
      if (characterSystem.avatarLoaded && characterSystem.avatarVB && characterSystem.avatarIB && characterSystem.boneMatricesBuffer && shadowCharacterBG) {
        shadowPass.setPipeline(shadowCharacterPipeline);
        shadowPass.setBindGroup(0, shadowCharacterBG);
        shadowPass.setVertexBuffer(0, characterSystem.avatarVB);
        shadowPass.setIndexBuffer(characterSystem.avatarIB, 'uint32');
        shadowPass.drawIndexed(characterSystem.avatarIndexCount);
      }

      // Render NPCs to shadow map
      if (npcSystem.isReady()) {
        // Create NPC bind groups once (lazy initialization)
        if (!npcBindGroupsCreated) {
          npcSystem.createBindGroups(
            characterLayout,
            shadowCharacterLayout,
            cameraBuffer,
            lightBuffer,
            shadowMapTexture,
            shadowMapSampler
          );
          npcBindGroupsCreated = true;
        }
        npcSystem.renderShadows(shadowPass, shadowCharacterPipeline);
      }

      shadowPass.end();

      // ============================================
      // Main Render Pass - Opaque objects (Sky, Terrain, Character)
      // ============================================
      const colorView = mainColorView;  // Always render to sceneTexture first
      const screenView = context.getCurrentTexture().createView();  // Always get screen for final output
      const depthView = depthTexture.createView();

      const opaquePass = enc.beginRenderPass({
        colorAttachments: [{
          view: colorView,
          clearValue: { r: 0.5, g: 0.7, b: 1.0, a: 1.0 },
          loadOp: 'clear', storeOp: 'store',
        }],
        depthStencilAttachment: {
          view: depthView,
          depthClearValue: 1.0, depthLoadOp: 'clear', depthStoreOp: 'store',
        },
      });

      // Sky
      opaquePass.setPipeline(skyPipeline);
      opaquePass.setBindGroup(0, skyBG);
      opaquePass.draw(6);

      // Terrain
      opaquePass.setPipeline(terrainPipeline);
      opaquePass.setBindGroup(0, terrainBG);
      opaquePass.setVertexBuffer(0, terrainVB);
      opaquePass.setIndexBuffer(terrainIB, 'uint32');
      opaquePass.drawIndexed(terrainBuffers.indexCount);

      // Character (avatar) - Rendered BEFORE ocean so depth buffer has character data
      if (characterSystem.avatarLoaded && characterSystem.avatarVB && characterSystem.avatarIB && characterSystem.avatarTexture && characterSystem.avatarSampler && characterSystem.boneMatricesBuffer) {
        // Update animation using character system
        const animState = {
          isSwimming, isSprintSwimming, isMoving, isFlying, isFalling, isJumping, isSprinting
        };
        characterSystem.updateAnimation(dt, animState);

        // Create bind group lazily when texture is ready
        if (!characterBG) {
          characterBG = device.createBindGroup({
            layout: characterLayout,
            entries: [
              { binding: 0, resource: { buffer: cameraBuffer } },
              { binding: 1, resource: { buffer: characterBuffer } },
              { binding: 2, resource: characterSystem.avatarTexture.createView() },
              { binding: 3, resource: characterSystem.avatarSampler },
              { binding: 4, resource: { buffer: characterSystem.boneMatricesBuffer } },
              { binding: 5, resource: { buffer: lightBuffer } },
              { binding: 6, resource: shadowMapTexture.createView() },
              { binding: 7, resource: shadowMapSampler },
            ],
          });
          console.log('✅ Character bind group created with animations and shadows!');
        }

        // Create shadow character bind group if needed
        if (!shadowCharacterBG) {
          shadowCharacterBG = device.createBindGroup({
            layout: shadowCharacterLayout,
            entries: [
              { binding: 0, resource: { buffer: lightBuffer } },
              { binding: 1, resource: { buffer: characterBuffer } },
              { binding: 2, resource: { buffer: characterSystem.boneMatricesBuffer } },
            ],
          });
          console.log('✅ Shadow character bind group created!');
        }

        opaquePass.setPipeline(characterPipeline);
        opaquePass.setBindGroup(0, characterBG);
        opaquePass.setVertexBuffer(0, characterSystem.avatarVB);
        opaquePass.setIndexBuffer(characterSystem.avatarIB, 'uint32');
        opaquePass.drawIndexed(characterSystem.avatarIndexCount);
      }

      // Render NPCs
      if (npcSystem.isReady()) {
        npcSystem.render(opaquePass, characterPipeline);
        
        // Render nameplates (Canvas2D billboards above characters)
        nameplateSystem.render(opaquePass);
      }

      opaquePass.end();

      // Create ocean bind group (done dynamically for resize support)
      if (!oceanBG) {
        oceanBG = device.createBindGroup({
          layout: oceanLayout,
          entries: [
            { binding: 0, resource: { buffer: cameraBuffer } },
            { binding: 1, resource: oceanBuffer },
            { binding: 2, resource: waterBumpTexture.createView() },
            { binding: 3, resource: textureSampler },
            { binding: 4, resource: heightmapView },
            { binding: 5, resource: heightmapSampler },
          ],
        });
        console.log('✅ Ocean bind group created with water bump texture!');
      }

      // ============================================
      // Transparent Pass - Ocean (reads depth from opaque pass)
      // ============================================
      const transparentPass = enc.beginRenderPass({
        colorAttachments: [{
          view: colorView,
          loadOp: 'load', storeOp: 'store',  // Keep existing color
        }],
        depthStencilAttachment: {
          view: depthView,
          depthLoadOp: 'load', depthStoreOp: 'store',  // Keep existing depth
        },
      });

      // Ocean - Rendered AFTER character for proper blending
      transparentPass.setPipeline(oceanPipeline);
      transparentPass.setBindGroup(0, oceanBG);
      transparentPass.setVertexBuffer(0, oceanVB);
      transparentPass.setIndexBuffer(oceanIB, 'uint32');
      transparentPass.drawIndexed(ocean.indices.length);

      // ============================================
      // Volumetric God Rays (only when underwater AND during day)
      // ============================================
      if (cameraUnderwater) {
        // Calculate day factor - god rays only visible during daytime
        // Same formula as shaders: dayFactor based on sun height
        const timeInCycle = (totalT % DAY_CYCLE_DURATION) / DAY_CYCLE_DURATION;  // 0-1
        const sunAngle = timeInCycle * Math.PI * 2 - Math.PI / 2;  // -PI/2 to 3PI/2
        const sunHeight = Math.sin(sunAngle);  // -1 at midnight, +1 at noon
        // dayFactor: 0 at night, 1 during day, smooth transition
        const dayFactor = Math.max(0, Math.min(1, (sunHeight + 0.1) / 0.4));

        // Update god rays buffer with day-adjusted intensity
        device.queue.writeBuffer(godRaysBuffer, 0, createGodRaysData({
          waterLevel: CONFIG.world.waterLevel,
          intensity: GOD_RAYS_CONFIG.intensity * dayFactor,
          numRays: GOD_RAYS_CONFIG.numRays,
          rayWidth: GOD_RAYS_CONFIG.rayWidth,
          rayLength: GOD_RAYS_CONFIG.rayLength,
          speed: GOD_RAYS_CONFIG.speed,
        }));

        transparentPass.setPipeline(godRaysPipeline);
        transparentPass.setBindGroup(0, godRaysBG);
        // Draw instances: 6 vertices per quad, numRays instances
        transparentPass.draw(6, GOD_RAYS_CONFIG.numRays);

        // ============================================
        // Underwater Bubbles - Beautiful translucent particles
        // ============================================
        device.queue.writeBuffer(bubblesBuffer, 0, createBubblesData({
          waterLevel: CONFIG.world.waterLevel,
          numBubbles: BUBBLES_CONFIG.numBubbles,
          minSize: BUBBLES_CONFIG.minSize,
          maxSize: BUBBLES_CONFIG.maxSize,
          riseSpeed: BUBBLES_CONFIG.riseSpeed,
          wobbleSpeed: BUBBLES_CONFIG.wobbleSpeed,
          wobbleAmount: BUBBLES_CONFIG.wobbleAmount,
          spawnRadius: BUBBLES_CONFIG.spawnRadius,
        }));

        transparentPass.setPipeline(bubblesPipeline);
        transparentPass.setBindGroup(0, bubblesBG);
        // Draw instances: 6 vertices per quad, numBubbles instances
        transparentPass.draw(6, BUBBLES_CONFIG.numBubbles);
      }

      // ============================================
      // WEATHER EFFECTS - Rain, Snow, Lightning
      // Rendered in transparent pass above water
      // ============================================
      if (weatherSystem.currentWeather >= 3 && !cameraUnderwater) {
        const wr = CONFIG.weather.rain;
        const ws = CONFIG.weather.snow;

        // Rain (weather 3, 4, 5)
        if (weatherSystem.currentWeather >= 3 && weatherSystem.currentWeather <= 5) {
          // Determine particle count based on intensity
          let particleCount = wr.lightParticles;
          if (weatherSystem.currentWeather === 4) particleCount = wr.heavyParticles;
          if (weatherSystem.currentWeather === 5) particleCount = wr.stormParticles;

          device.queue.writeBuffer(weatherSystem.rainBuffer, 0, createRainData({
            numParticles: particleCount,
            speed: wr.speed,
            length: wr.length,
            width: wr.width,
            windStrength: wr.windStrength * (weatherSystem.currentWeather === 5 ? 1.5 : 1.0),
            spawnRadius: wr.spawnRadius,
            spawnHeight: wr.spawnHeight,
            intensity: weatherSystem.weatherIntensity,
            color: wr.color,
            opacity: wr.opacity * weatherSystem.weatherIntensity,
            lightningFlash: weatherSystem.lightningFlash,
          }));

          transparentPass.setPipeline(weatherSystem.rainPipeline);
          transparentPass.setBindGroup(0, weatherSystem.rainBG);
          transparentPass.draw(6, Math.floor(particleCount * weatherSystem.weatherIntensity));
        }

        // Snow (weather 6)
        if (weatherSystem.currentWeather === 6) {
          device.queue.writeBuffer(weatherSystem.snowBuffer, 0, createSnowData({
            numParticles: ws.particles,
            speed: ws.speed,
            wobbleSpeed: ws.wobbleSpeed,
            wobbleAmount: ws.wobbleAmount,
            minSize: ws.minSize,
            maxSize: ws.maxSize,
            spawnRadius: ws.spawnRadius,
            spawnHeight: ws.spawnHeight,
            color: ws.color,
            opacity: ws.opacity * weatherSystem.weatherIntensity,
          }));

          transparentPass.setPipeline(weatherSystem.snowPipeline);
          transparentPass.setBindGroup(0, weatherSystem.snowBG);
          transparentPass.draw(6, Math.floor(ws.particles * weatherSystem.weatherIntensity));
        }

        // Lightning bolts (weather 5, only when active)
        if (weatherSystem.currentWeather === 5 && weatherSystem.lightningActive) {
          const lc = CONFIG.weather.lightning;

          device.queue.writeBuffer(weatherSystem.lightningBuffer, 0, createLightningData({
            isActive: 1.0,
            startTime: weatherSystem.lightningStartTime,
            boltDuration: lc.boltDuration,
            flashIntensity: lc.flashIntensity,
            startX: weatherSystem.lightningX,
            startZ: weatherSystem.lightningZ,
            seed: weatherSystem.lightningSeed,
            numBranches: lc.boltBranches,
          }));

          transparentPass.setPipeline(weatherSystem.lightningPipeline);
          transparentPass.setBindGroup(0, weatherSystem.lightningBG);
          // Volumetric lightning: 16 segments × 3 glow layers × 6 bolts (2 main + 4 branches)
          transparentPass.draw(6, 16 * 3 * 6);
        }
      }

      transparentPass.end();

      // ============================================
      // Underwater Effect Pass (fullscreen post-process)
      // ============================================
      if (cameraUnderwater) {
        // Create bind group for underwater effect
        const underwaterBG = device.createBindGroup({
          layout: underwaterLayout,
          entries: [
            { binding: 0, resource: { buffer: underwaterBuffer } },
            { binding: 1, resource: sceneTexture.createView() },
            { binding: 2, resource: sceneSampler },
          ],
        });

        const underwaterPass = enc.beginRenderPass({
          colorAttachments: [{
            view: screenView,
            loadOp: 'clear', storeOp: 'store',
          }],
        });

        underwaterPass.setPipeline(underwaterPipeline);
        underwaterPass.setBindGroup(0, underwaterBG);
        underwaterPass.draw(3);  // Fullscreen triangle

        underwaterPass.end();
      } else {
        // Not underwater - reset underwater buffer with cameraDepth = 0 to disable blur
        const uw = CONFIG.underwater;
        device.queue.writeBuffer(underwaterBuffer, 0, createUnderwaterData({
          cameraPos: camPos,
          time: totalT,
          waterLevel: waterLevel,
          cameraDepth: 0.0,
          fogDensity: uw.fogDensity,
          fogStart: uw.fogStart,
          fogEnd: uw.fogEnd,
          tintStrength: uw.tintStrength,
          godRaysIntensity: uw.godRaysIntensity,
          godRaysSpeed: uw.godRaysSpeed,
          godRaysDensity: uw.godRaysDensity,
          godRaysLength: uw.godRaysLength,
          causticsIntensity: uw.causticsIntensity,
          causticsScale: uw.causticsScale,
          causticsSpeed: uw.causticsSpeed,
          surfaceWaveStrength: uw.surfaceWaveStrength,
          surfaceFresnelPower: uw.surfaceFresnelPower,
          bubblesEnabled: 0.0,
          bubblesCount: uw.bubblesCount,
          bubblesSpeed: uw.bubblesSpeed,
          bubblesSize: uw.bubblesSize,
          depthDarkenStart: uw.depthDarkenStart,
          depthDarkenEnd: uw.depthDarkenEnd,
          depthDarkenStrength: uw.depthDarkenStrength,
          blurRadius: uw.blurRadius,
          fogColor: uw.fogColor,
          tintColor: uw.tintColor,
          godRaysColor: uw.godRaysColor,
          surfaceColor: uw.surfaceColor,
          bubblesColor: uw.bubblesColor,
        }));

        // Blit sceneTexture to screen using a simple pass
        const blitBG = device.createBindGroup({
          layout: underwaterLayout,
          entries: [
            { binding: 0, resource: { buffer: underwaterBuffer } },
            { binding: 1, resource: sceneTexture.createView() },
            { binding: 2, resource: sceneSampler },
          ],
        });

        const blitPass = enc.beginRenderPass({
          colorAttachments: [{
            view: screenView,
            loadOp: 'clear', storeOp: 'store',
          }],
        });

        // Use underwater pipeline but with cameraUnderwater=false the effect is minimal
        blitPass.setPipeline(underwaterPipeline);
        blitPass.setBindGroup(0, blitBG);
        blitPass.draw(3);
        blitPass.end();
      }

      device.queue.submit([enc.finish()]);

      requestAnimationFrame(render);
    }

    // Resize handler
    window.addEventListener('resize', () => {
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      depthTexture.destroy();
      depthTexture = device.createTexture({
        size: [canvas.width, canvas.height],
        format: 'depth32float',
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
      });
      // Recreate scene texture for underwater effect
      sceneTexture.destroy();
      sceneTexture = device.createTexture({
        size: [canvas.width, canvas.height],
        format,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC,
      });
      // Reset ocean bind group so it gets recreated with new textures
      oceanBG = null;
    });

    console.log('🌍 WebGPU World started!');
  